/* eslint-disable no-param-reassign */
import { navigateTo, useRoute } from '#imports'
import { type Ref, computed, watch } from 'vue'

import type {
  ConfigurationFacet,
  Facet,
} from '@backmarket/nuxt-layer-recommendation/filtering/types'

import {
  getOpenFilterHashRedirect,
  getThinResultsRedirect,
  getUnknownQueryKeyRedirect,
} from '../utils/filterRedirects'
import { fullyDecodeUriComponent } from '../utils/fullyDecodeUriComponent'

const POSITION_PREFIX_RE = /^\d+\s+(.+)$/
const QUERY_FILTER_TITLE_SEPARATOR = ' • '
const FACET_LABEL_VALUE_SEPARATOR = ' : '

export function useOpenFilter(
  openFacets: Ref<string[]>,
  openFacetsError: Ref<unknown>,
  pageData?: {
    pageTitle: string
    configBreadcrumb: { label: string; url: string }[]
    facets: Ref<Facet[]>
    configFacets?: Ref<ConfigurationFacet[]>
  },
) {
  const route = useRoute()

  const openFilter = computed(() => {
    const key = openFacets.value.find((facet) => route.query[facet] != null)
    if (!key) return null
    const raw = route.query[key]
    const value = fullyDecodeUriComponent(
      Array.isArray(raw) ? (raw[0] ?? '') : (raw ?? ''),
    )
    if (!value) return null

    const facetLabel = pageData?.facets.value
      .find((f) => f.name === key)
      ?.values.find((v) => v.name === value)?.label

    const label = facetLabel ?? value.replace(POSITION_PREFIX_RE, '$1')

    return { key, value, label }
  })

  function routeContext() {
    return { path: route.path, query: route.query, hash: route.hash }
  }

  async function redirectInvalidFilters() {
    const allFacetKeys =
      pageData?.configFacets?.value.map((facet) => facet.name) ??
      openFacets.value
    const unknownQueryKeyRedirect = getUnknownQueryKeyRedirect(
      routeContext(),
      openFilter.value?.key ?? null,
      Boolean(openFacetsError.value),
      allFacetKeys,
    )
    if (unknownQueryKeyRedirect) {
      await navigateTo(unknownQueryKeyRedirect, { redirectCode: 301 })

      return
    }

    const openFilterHashRedirect = getOpenFilterHashRedirect(
      routeContext(),
      openFilter.value?.key ?? null,
    )
    if (openFilterHashRedirect) {
      await navigateTo(openFilterHashRedirect, { redirectCode: 301 })
    }
  }

  async function redirectThinResults(total: number) {
    const thinResultsRedirect = getThinResultsRedirect(
      routeContext(),
      openFilter.value?.key ?? null,
      total,
    )
    if (thinResultsRedirect) {
      await navigateTo(thinResultsRedirect, { redirectCode: 302 })
    }
  }

  function watchOpenFilter(filtersInput: Record<string, string[]>) {
    watch(
      openFilter,
      (current, previous) => {
        if (
          current &&
          (current.key !== previous?.key || current.value !== previous?.value)
        ) {
          for (const key of Object.keys(filtersInput)) {
            if (key !== current.key) {
              filtersInput[key] = []
            }
          }
        }

        if (previous?.key && previous.key !== current?.key) {
          const prevValues = filtersInput[previous.key]
          if (prevValues?.length === 1 && prevValues[0] === previous.value) {
            filtersInput[previous.key] = []
          }
        }

        if (current) {
          filtersInput[current.key] = [current.value]
        }
      },
      { flush: 'sync' },
    )
  }

  const configFacet = computed(() =>
    pageData?.configFacets?.value.find(
      (facet) => facet.name === openFilter.value?.key,
    ),
  )

  const openFilterLabel = computed(() => {
    if (!openFilter.value) return ''
    return configFacet.value?.type === 'radio' && configFacet.value.title
      ? `${configFacet.value.title}${FACET_LABEL_VALUE_SEPARATOR}${openFilter.value.label}`
      : openFilter.value.label
  })

  const title = computed(() => {
    if (!pageData || !openFilter.value) return pageData?.pageTitle ?? ''

    return `${pageData.pageTitle}${QUERY_FILTER_TITLE_SEPARATOR}${openFilterLabel.value}`
  })

  const breadcrumb = computed(() => {
    if (!pageData || !openFilter.value) return pageData?.configBreadcrumb ?? []

    return [
      ...pageData.configBreadcrumb,
      {
        label: openFilterLabel.value,
        url: `${route.path}?${openFilter.value.key}=${encodeURIComponent(openFilter.value.value)}`,
      },
    ]
  })

  return {
    openFilter,
    openFilterLabel,
    redirectInvalidFilters,
    redirectThinResults,
    watchOpenFilter,
    title,
    breadcrumb,
  }
}
